import { useState, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, Plus, X } from "lucide-react";

/**
 * MultiSelectWithCustom - A component that allows both selection from predefined options
 * AND manual addition of custom values
 */
export default function MultiSelectWithCustom({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select...",
  single = false,
  className = "",
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [customOptions, setCustomOptions] = useState([]);
  const containerRef = useRef(null);

  // Combine predefined and custom options
  const allOptions = [...new Set([...options, ...customOptions])];

  // Filter options based on search
  const filteredOptions = allOptions.filter(opt =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if search term is a new custom value
  const isNewValue = searchTerm.trim() && 
    !allOptions.some(opt => opt.toLowerCase() === searchTerm.toLowerCase().trim());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value) => {
    if (single) {
      onChange([value]);
      setIsOpen(false);
    } else {
      if (selected.includes(value)) {
        onChange(selected.filter(v => v !== value));
      } else {
        onChange([...selected, value]);
      }
    }
    setSearchTerm("");
  };

  const handleAddCustom = () => {
    const newValue = searchTerm.trim();
    if (newValue && !allOptions.includes(newValue)) {
      setCustomOptions(prev => [...prev, newValue]);
      handleSelect(newValue);
    }
  };

  const handleRemove = (value, e) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isNewValue) {
      e.preventDefault();
      handleAddCustom();
    }
  };

  return (
    <div ref={containerRef} className={`relative mt-1 ${className}`} {...props}>
      {/* Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="min-h-[44px] px-3 py-2 border border-gray-200 rounded-lg bg-white cursor-pointer flex flex-wrap items-center gap-2 hover:border-violet-300 transition-colors"
      >
        {selected.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : single ? (
          <span className="text-gray-900">{selected[0]}</span>
        ) : (
          selected.map(val => (
            <Badge 
              key={val} 
              variant="secondary" 
              className="bg-violet-100 text-violet-800 px-2 py-1 flex items-center gap-1"
            >
              {val}
              <button
                onClick={(e) => handleRemove(val, e)}
                className="hover:text-violet-600"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or add custom..."
              className="h-9"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 && !isNewValue ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                No options found
              </div>
            ) : (
              <>
                {filteredOptions.map(option => (
                  <div
                    key={option}
                    onClick={() => handleSelect(option)}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-violet-50 ${
                      selected.includes(option) ? 'bg-violet-50 text-violet-700' : ''
                    }`}
                  >
                    <span>{option}</span>
                    {selected.includes(option) && <Check className="w-4 h-4" />}
                  </div>
                ))}
              </>
            )}

            {/* Add Custom Option */}
            {isNewValue && (
              <div
                onClick={handleAddCustom}
                className="px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-teal-50 text-teal-700 border-t border-gray-100"
              >
                <Plus className="w-4 h-4" />
                <span>Add "{searchTerm.trim()}"</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
